function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useStoreActions } from "../../contexts";
import { useLocaleKeys } from "../../i18n";
export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
export const DEFAULT_MAX_TOTAL_SIZE = 24 * 1024 * 1024; // 24MB in bytes

export const AttachFilesTestIds = getTestIds('AttachFilesTestIds');
export function useAttachFiles() {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
    initialAttachments = [],
    fetchAttachmentUrl,
    onFilesUploaded,
    onRemove
  } = params;
  const {
    api
  } = useStoreActions();
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    uploadFileToCloudStorage
  } = useIoContext();
  const {
    t
  } = useLocaleKeys('invoice.attachments.uploadErrors');
  const [isUploading, setIsUploading] = useState(false);
  const [attachments, setAttachments] = useState(() => initialAttachments.map(att => _objectSpread(_objectSpread({}, att), {}, {
    loading: !att.url
  })));
  const urlCacheRef = useRef({});
  const fetchAttachmentUrlRef = useRef(fetchAttachmentUrl);
  fetchAttachmentUrlRef.current = fetchAttachmentUrl;
  const fetchUrlsForAttachments = attachmentIds => {
    if (!fetchAttachmentUrlRef.current || attachmentIds.length === 0) return;
    attachmentIds.forEach(async id => {
      if (urlCacheRef.current[id]) {
        setAttachments(prev => prev.map(att => att.id === id ? _objectSpread(_objectSpread({}, att), {}, {
          url: urlCacheRef.current[id],
          loading: false
        }) : att));
        return;
      }
      try {
        const url = await fetchAttachmentUrlRef.current(id);
        urlCacheRef.current[id] = url; // Cache the URL
        setAttachments(prev => prev.map(att => att.id === id ? _objectSpread(_objectSpread({}, att), {}, {
          url,
          loading: false
        }) : att));
      } catch (_unused) {
        setAttachments(prev => prev.map(att => att.id === id ? _objectSpread(_objectSpread({}, att), {}, {
          loading: false
        }) : att));
      }
    });
  };
  const setCurrentAttachments = useCallback(newAttachments => {
    const attachmentsWithCachedUrls = newAttachments.map(att => {
      const cachedUrl = urlCacheRef.current[att.id];
      return _objectSpread(_objectSpread({}, att), {}, {
        url: cachedUrl || att.url,
        loading: !cachedUrl && !att.url
      });
    });
    setAttachments(attachmentsWithCachedUrls);
    const attachmentIdsNeedingUrl = attachmentsWithCachedUrls.filter(att => !att.url).map(att => att.id);
    fetchUrlsForAttachments(attachmentIdsNeedingUrl);
  }, []);
  useEffect(() => {
    const attachmentIdsNeedingUrl = initialAttachments.filter(att => !att.url).map(att => att.id);
    fetchUrlsForAttachments(attachmentIdsNeedingUrl);
  }, [initialAttachments]);
  const uploadFilesToGCS = async files => {
    const filesMap = {};
    const filesData = [];
    files.forEach(file => {
      const id = generateUniqueId('attachment');
      filesData.push({
        id,
        displayName: file.name,
        size: file.size
      });
      filesMap[id] = file;
    });
    const {
      urls
    } = await api.business.getUploadUrls(filesData);
    await Promise.all(urls.map(_ref => {
      let {
        id,
        url,
        headers
      } = _ref;
      const file = filesMap[id];
      if (!file) throw new Error("File not found for id ".concat(id));
      return uploadFileToCloudStorage(url, file, headers);
    }));
    return filesData;
  };
  const handleUpload = async files => {
    const loadingAttachments = files.map(file => ({
      id: generateUniqueId('temp'),
      displayName: file.name,
      fileSize: file.size,
      loading: true
    }));
    setAttachments(prev => [...prev, ...loadingAttachments]);
    setIsUploading(true);
    try {
      const uploadedData = await uploadFilesToGCS(files);
      await (onFilesUploaded === null || onFilesUploaded === void 0 ? void 0 : onFilesUploaded(uploadedData));
      const uploadedAttachments = uploadedData.map(_ref2 => {
        let {
          id,
          displayName,
          size
        } = _ref2;
        return {
          id,
          displayName,
          fileSize: size,
          loading: !!fetchAttachmentUrl
        };
      });
      setAttachments(prev => {
        const nonLoading = prev.filter(att => !loadingAttachments.some(la => la.id === att.id));
        return [...nonLoading, ...uploadedAttachments];
      });
      fetchUrlsForAttachments(uploadedData.map(_ref3 => {
        let {
          id
        } = _ref3;
        return id;
      }));
    } catch (error) {
      setAttachments(prev => prev.filter(att => !loadingAttachments.some(la => la.id === att.id)));
      showErrorToast(t.uploadFailed(), AttachFilesTestIds.UploadFailedErrorToast());
    } finally {
      setIsUploading(false);
    }
  };
  const processFiles = files => {
    if (!files || files.length === 0) return;
    const allFiles = Array.from(files);
    const validFiles = allFiles.filter(file => {
      var _file$name$split$pop;
      const extension = (_file$name$split$pop = file.name.split('.').pop()) === null || _file$name$split$pop === void 0 ? void 0 : _file$name$split$pop.toLowerCase();
      return extension && ALLOWED_EXTENSIONS.includes(extension);
    });
    if (validFiles.length !== allFiles.length) {
      showErrorToast(t.unsupportedFileType(), AttachFilesTestIds.UnsupportedFileTypeErrorToast());
      return;
    }
    const totalSize = attachments.reduce((sum, file) => sum + file.fileSize, 0) + validFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxTotalSize) {
      showErrorToast(t.totalSizeExceeded({
        maxSizeMB: maxTotalSize / (1024 * 1024)
      }), AttachFilesTestIds.TotalSizeExceededErrorToast());
      return;
    }
    const existingFileNames = new Set(attachments.map(att => att.displayName));
    const newFileNames = validFiles.map(file => file.name);
    const hasDuplicateWithExisting = newFileNames.some(name => existingFileNames.has(name));
    const hasDuplicateWithinBatch = new Set(newFileNames).size !== newFileNames.length;
    if (hasDuplicateWithExisting || hasDuplicateWithinBatch) {
      showErrorToast(t.duplicateFileName(), AttachFilesTestIds.DuplicateFileNameErrorToast());
      return;
    }
    handleUpload(validFiles);
  };
  const openFilePicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ALLOWED_EXTENSIONS.map(ext => ".".concat(ext)).join(',');
    input.onchange = () => {
      processFiles(input.files);
      input.remove();
    };
    input.click();
  };
  const handleRemove = useCallback(async index => {
    const attachment = attachments[index];
    if (!attachment || attachment.loading) return;
    const previousAttachments = attachments;
    setAttachments(prev => prev.filter((_, i) => i !== index));
    try {
      await (onRemove === null || onRemove === void 0 ? void 0 : onRemove(attachment));
    } catch (_unused2) {
      setAttachments(previousAttachments);
      showErrorToast(t.removeFailed(), AttachFilesTestIds.RemoveFailedErrorToast());
    }
  }, [attachments, onRemove, showErrorToast, t]);
  return {
    attachments,
    openFilePicker,
    processFiles,
    handleRemove,
    allowedExtensions: ALLOWED_EXTENSIONS,
    isUploading,
    setCurrentAttachments
  };
}